// WP-07 — SessionStore (LLD-05 §4): Zustand with `persist` for token/session
// ONLY (LLD-05 §11 — TokenStore is memory + guarded persist; logout clears
// both). Server data never lives here — that is React Query's job (§2.4).
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { OrgSummaryDto, Tokens, UserProfile } from "../api/contracts";
import type { Role } from "./roles";

export interface Session {
  user: UserProfile;
  role: Role;
  tokens: Tokens;
  /** WP-22 (D-F5): tenant identity + branding for app-wide theming. Display
   * only — never an authority source (the server owns tenant scope via the JWT
   * tid); persisted so a cold reload re-applies the tenant theme. */
  org?: OrgSummaryDto;
}

export interface SessionState {
  user: UserProfile | null;
  role: Role | null;
  tokens: Tokens | null;
  org: OrgSummaryDto | null;
  setSession: (session: Session) => void;
  /** Token rotation on refresh — user/role unchanged. */
  setTokens: (tokens: Tokens) => void;
  clearSession: () => void;
}

export const SESSION_STORAGE_KEY = "steier.office.session";

export const sessionStore = createStore<SessionState>()(
  persist(
    (set) => ({
      user: null,
      role: null,
      tokens: null,
      org: null,
      setSession: (session) =>
        set({
          user: session.user,
          role: session.role,
          tokens: session.tokens,
          org: session.org ?? null,
        }),
      setTokens: (tokens) => set({ tokens }),
      clearSession: () => set({ user: null, role: null, tokens: null, org: null }),
    }),
    {
      name: SESSION_STORAGE_KEY,
      storage: createJSONStorage(() => window.localStorage),
      partialize: ({ user, role, tokens, org }) => ({ user, role, tokens, org }),
    },
  ),
);

/** React binding; non-React modules use sessionStore.getState() directly. */
export function useSession<T>(selector: (state: SessionState) => T): T {
  return useStore(sessionStore, selector);
}
